import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiShare2, FiCheck, FiX, FiExternalLink } from 'react-icons/fi';
import { FaLinkedinIn } from 'react-icons/fa';
import { IGlobalState } from '../../../model/IGlobalState';
import { SocialAccountsResult } from '../../../model/SocialAccountsResult';
import { AnnouncementRow, CommunicationsAnnouncementsResult } from '../../../model/CommunicationsAnnouncementsResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { getExternalLink } from '../../stock/AnnouncementTable';
import Dropdown from '../Dropdown';
import { toast } from '../Toast';
import ContactsModal from './contacts/ContactsModal';

interface Props {
    from?: AnnouncementRow; // opened from an announcement's ⋯ menu: draft from this one instead of the latest
    onClose: () => void;
}

type Net = 'linkedin' | 'x';
const LIMITS: Record<Net, number> = { linkedin: 3000, x: 280 };
const NAMES: Record<Net, string> = { linkedin: 'LinkedIn', x: 'X' };
const CUSTOM = '';
const RECENT = 30;

// what happened to one network's post: url is null when it posted but the network didn't say where
interface Posted { net: Net; url?: string | null; error?: string }

// X counts every link as 23 characters, whatever its length
const xLength = (text: string) => text.replace(/https?:\/\/\S+/g, 'x'.repeat(23)).length;
const linkOf = (row: AnnouncementRow) => (row.localPath && getExternalLink({ localPath: row.localPath })) || '';
const keyOf = (r: AnnouncementRow) => r.date + r.headline;
const initials = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
const errorText = (e: any, fallback: string) => typeof e?.response?.data === 'string' && e.response.data ? e.response.data : fallback;

// spec "v3 / Communications – New post" (frame holding 2:55754), as a pop-up; also opened from an announcement's
// ⋯ "Create a social post" (2:53531). Real: the client's recent announcements (GET /api/communications/announcements)
// for the draft text and link, the connected accounts, and Post Now to LinkedIn and X. Facebook left out (user decision).
// Save Draft is preview only. Omitted (no source): Schedule Post, follower counts, ✦ Rewrite / Shorter for X (no AI in v2),
// picture upload, best-time hint, Approval card, link card (LinkedIn needs an article post for one).
const NewSocialPostModal: React.FC<Props> = ({ from, onClose }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const name = stock?.companyName || stock?.ticker || '';
    const ticker = stock?.ticker ?? '';
    const [accounts, setAccounts] = useState<SocialAccountsResult | null>(null);
    const [rows, setRows] = useState<AnnouncementRow[] | null>(null);
    const [picked, setPicked] = useState(CUSTOM);
    const [text, setText] = useState('');
    const [nets, setNets] = useState<Net[]>([]);
    const [view, setView] = useState<Net>('linkedin');
    const [posting, setPosting] = useState(false);
    const [posted, setPosted] = useState<Posted[] | null>(null);

    const x = accounts?.x ?? null;
    const linkedIn = accounts?.linkedIn?.companyName ? accounts.linkedIn : null;
    const draftFor = (row: AnnouncementRow) => [row.headline, `Read the full announcement: ${linkOf(row)}`, `#ASX #${ticker}`].join('\n\n');

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        const [accountsErr, accountsData] = await to(api().getSocialAccounts(stock.id));
        const [err, data] = await to(api().getCommunicationsAnnouncements(stock.id));
        if (!isMounted()) return;
        if (accountsErr) toast.error('Couldn’t load the connected social accounts');
        const connected = accountsErr ? null : accountsData as SocialAccountsResult;
        setAccounts(connected);
        // every connected account starts ticked
        setNets([...(connected?.linkedIn?.companyName ? ['linkedin' as Net] : []), ...(connected?.x ? ['x' as Net] : [])]);
        const recent = err ? [] : (data as CommunicationsAnnouncementsResult).rows.filter(r => !r.deletedOn).slice(0, RECENT);
        if (err) toast.error('Couldn’t load your announcements');
        // an older announcement picked from the ⋯ menu isn't in the recent list, so put it first
        const list = from && !recent.some(r => keyOf(r) === keyOf(from)) ? [from, ...recent] : recent;
        setRows(list);
        // start from that announcement, else the latest, like the spec's "generated from …" draft
        const start = from ?? list[0];
        if (start) {
            setPicked(keyOf(start));
            setText(draftFor(start));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stock?.id]);

    const row = rows?.find(r => keyOf(r) === picked) ?? null;
    const options = useMemo(() => [
        { value: CUSTOM, label: 'No announcement (write your own)' },
        ...(rows ?? []).map(r => ({ value: keyOf(r), label: `${moment(r.date).format('D MMM')} · ${r.headline}` })),
    ], [rows]);

    const pick = (value: string) => {
        setPicked(value);
        const next = rows?.find(r => keyOf(r) === value);
        setText(next ? draftFor(next) : '');
    };

    const toggle = (n: Net) => setNets(list => list.includes(n) ? list.filter(v => v !== n) : [...list, n]);
    const addHashtags = () => {
        const tags = ['#ASX', `#${ticker}`].filter(t => !text.includes(t));
        if (tags.length) setText(`${text.trimEnd()}\n\n${tags.join(' ')}`);
    };

    const xLen = xLength(text);
    const xOver = nets.includes('x') && xLen > LIMITS.x;
    const linkedInOver = nets.includes('linkedin') && text.length > LIMITS.linkedin;
    const ready = !posting && !posted && nets.length > 0 && text.trim() !== '' && !xOver && !linkedInOver;

    // one network failing doesn't stop the other
    const postNow = async () => {
        if (!stock?.id) return;
        setPosting(true);
        const results: Posted[] = [];
        for (const net of nets) {
            try {
                const result = net === 'x' ? await api().postX(stock.id, text) : await api().postLinkedIn(stock.id, text);
                results.push({ net, url: result.url });
            } catch (e) {
                results.push({ net, error: errorText(e, `${NAMES[net]} didn’t take the post`) });
            }
        }
        setPosting(false);
        setPosted(results);
        const ok = results.filter(r => !r.error).map(r => NAMES[r.net]);
        if (ok.length) toast.success(`Posted to ${ok.join(' and ')}`);
        if (ok.length < results.length) toast.error('Some posts didn’t go out');
    };

    const channel = (n: Net, label: string, detail: string, connected: boolean) => {
        const on = connected && nets.includes(n);
        return <button type="button" role="checkbox" aria-checked={on} disabled={!connected || !!posted}
            className={`nsp-channel${on ? ' on' : ''}${connected ? '' : ' off'}`} onClick={() => toggle(n)}>
            <span className="nsp-check">{on && <FiCheck />}</span>
            {/* react-icons 3 has no X logo, so X is the letter on its black tile */}
            <span className={`nsp-net ${n}`}>{n === 'x' ? '𝕏' : <FaLinkedinIn />}</span>
            <span className="nsp-channel-t"><strong>{label}</strong><small>{detail}</small></span>
        </button>;
    };

    return <ContactsModal wide className="nsp-modal" icon={<FiShare2 />} title="New Social Post"
        sub={row ? `Draft · generated from “${row.headline}”` : 'A post for LinkedIn and X'}
        onClose={onClose}
        buttons={posted
            ? <button type="button" className="btn-primary" onClick={onClose}>Done</button>
            : <>
                <button type="button" className="btn-outline nsp-draft" onClick={() => toast.info('Preview only: saving drafts isn’t connected yet')}>Save Draft</button>
                <button type="button" className="btn-primary" disabled={!ready} onClick={postNow}>
                    <FiShare2 /> {posting ? 'Posting…' : 'Post Now'}
                </button>
            </>}>
        <div className="new-social-post">
            <div className="nsp-composer">
                <div className="nsp-field">
                    <span className="nsp-label">From announcement</span>
                    {rows === null
                        ? <div className="nsp-loading">Loading announcements…</div>
                        : <Dropdown value={picked} options={options} onChange={pick} searchable />}
                </div>

                <div className="nsp-channels">
                    {channel('linkedin', 'LinkedIn', linkedIn ? linkedIn.companyName! : 'Not connected', !!linkedIn)}
                    {channel('x', 'X', x ? `@${x.username}` : 'Not connected', !!x)}
                </div>

                <div className="nsp-field">
                    <span className="nsp-label">What the post says</span>
                    <textarea rows={8} value={text} onChange={e => setText(e.target.value)} aria-label="What the post says" placeholder="Write your post…" readOnly={!!posted} />
                    <div className="nsp-meta">
                        <span><span className={linkedInOver ? 'nsp-over' : ''}>LinkedIn {text.length.toLocaleString()} / {LIMITS.linkedin.toLocaleString()}</span> · <span className={xOver ? 'nsp-over' : ''}>X {xLen} / {LIMITS.x}</span></span>
                        {!posted && <button type="button" className="nsp-chip" onClick={addHashtags}>Add hashtags</button>}
                    </div>
                </div>

                {posted && <div className="nsp-posted">
                    {posted.map(p => <div key={p.net}>
                        {p.error
                            ? <><FiX className="failed" /> {NAMES[p.net]}: {p.error}</>
                            : <><FiCheck className="ok" /> Posted to {NAMES[p.net]}
                                {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer">View <FiExternalLink /></a>}</>}
                    </div>)}
                </div>}
            </div>

            <div className="nsp-preview">
                <div className="nsp-preview-h">
                    <strong>Preview</strong>
                    <div className="nsp-seg">
                        <button type="button" className={view === 'linkedin' ? 'on' : ''} onClick={() => setView('linkedin')}>LinkedIn</button>
                        <button type="button" className={view === 'x' ? 'on' : ''} onClick={() => setView('x')}>X</button>
                    </div>
                </div>
                <div className="nsp-post">
                    <div className="nsp-post-h">
                        {stock?.companyLogoUrl
                            ? <img className="nsp-avatar" src={stock.companyLogoUrl} alt="" />
                            : <span className="nsp-avatar">{initials(name)}</span>}
                        <div>
                            <strong>{view === 'linkedin' && linkedIn ? linkedIn.companyName : name}</strong>
                            <small>{view === 'x' ? `${x ? `@${x.username}` : 'X not connected'} · Just now` : 'Company page · Just now'}</small>
                        </div>
                    </div>
                    <div className="nsp-post-text">{text.trim() || <em>Your post appears here</em>}</div>
                </div>
            </div>
        </div>
    </ContactsModal>;
};

export default NewSocialPostModal;
